import React, { useState, useMemo } from "react";
import { Activity, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { ActivityFeed } from "../dashboard/ActivityFeed";
import { cn } from "@/lib/utils";
import {
  ACTIVITY_CATEGORIES,
  EVENT_TYPES_BY_CATEGORY,
  type ActivityEventCategory,
  type ActivityEventSlug,
} from "../../types/activity.types";

interface ActivityDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ActivityDrawer: React.FC<ActivityDrawerProps> = ({
  open,
  onOpenChange,
}) => {
  const [activeCategory, setActiveCategory] =
    useState<ActivityEventCategory>("all");

  const eventTypes: ActivityEventSlug[] | undefined = useMemo(() => {
    if (activeCategory === "all") return undefined;
    return EVENT_TYPES_BY_CATEGORY[activeCategory];
  }, [activeCategory]);

  const clearFilter = () => setActiveCategory("all");

  // Always show all activity for the current user, regardless of project selection.
  const scope: { projectId: string } | "user" = "user";

  const description = "Recent activity";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-md w-full p-0 flex flex-col"
        hideCloseButton
      >
        <SheetHeader className="px-6 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Activity
            </SheetTitle>
            <Button
              variant="utility"
              size="icon"
              onClick={() => onOpenChange(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>

        {/* Category filter pills */}
        <div className="flex gap-1.5 px-6 pb-2 flex-wrap">
          {ACTIVITY_CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={cn(
                "h-8 px-2.5 text-xs rounded-[6px] border transition-all cursor-pointer",
                activeCategory === cat.key
                  ? "bg-[rgba(var(--color-primitive-pink-500-rgb),0.15)] text-[var(--color-primitive-pink-500)] border-[rgba(var(--color-primitive-pink-500-rgb),0.4)]"
                  : "bg-white/5 text-[var(--color-primitive-neutral-200)] border-white/12 hover:bg-white/10 hover:border-white/20 hover:text-white",
              )}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <ActivityFeed
            scope={scope}
            eventTypes={eventTypes}
            activeCategory={activeCategory}
            onClearFilter={clearFilter}
            onClose={() => onOpenChange(false)}
            open={open}
            className="h-full"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
